import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  Renderer2,
  ElementRef,
  viewChild,
} from '@angular/core';
import { ContactService } from '@services/contact/contact.service';
import { PopupModalService } from '../../services/popup-modal/popup-modal.service';
import { CmsService } from '@services/cms/cms.service';

@Component({
  selector: 'app-contact-fab',
  standalone: true,
  templateUrl: './contact-fab.component.html',
  styleUrl: './contact-fab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactFabComponent {
  fabMenu = viewChild<ElementRef<HTMLElement>>('fabMenu');

  readonly contactService = inject(ContactService);
  private popupModalService = inject(PopupModalService);
  cmsService = inject(CmsService);
  private renderer = inject(Renderer2);

  isFabOpen = signal(false);
  expandDirection = signal<'up' | 'down'>('down');
  skipCloseAnimation = signal(false);
  private lastAnchor: HTMLElement | null = null;

  async openContact(mode: 'call' | 'text' | 'email'): Promise<void> {
    this.contactService.setType(mode);

    const { ContactComponent } = await import(
      '@components/modals/contact/contact.component'
    );
    this.popupModalService.open(ContactComponent);
  }

  close() {
    this.skipCloseAnimation.set(false);
    this.isFabOpen.set(false);
    this.lastAnchor = null;
  }

  openAt(target: HTMLElement, alignment: string = 'left'): void {
    const menu = this.fabMenu()?.nativeElement;

    if (typeof window === 'undefined' || !menu) {
      return;
    }

    if (this.isFabOpen() && this.lastAnchor === target) {
      this.close();
      return;
    }

    const rect = target.getBoundingClientRect();
    const expandDown = rect.top < window.innerHeight / 2;
    const nextDirection = expandDown ? 'down' : 'up';
    this.expandDirection.set(nextDirection);

    const top = expandDown ? rect.bottom + 10 : rect.top - 10;
    const left =
      alignment === 'right'
        ? rect.right
        : alignment === 'center'
          ? rect.left + rect.width / 2
          : rect.left;

    const positionMenu = (): void => {
      this.expandDirection.set(nextDirection);
      this.renderer.setStyle(menu, 'top', `${top}px`);
      this.renderer.setStyle(menu, 'left', `${left}px`);
      this.lastAnchor = target;
    };

    const replayOpen = (): void => {
      requestAnimationFrame(() => {
        this.isFabOpen.set(true);
        this.skipCloseAnimation.set(false);
      });
    };

    if (this.isFabOpen()) {
      this.skipCloseAnimation.set(true);
      this.isFabOpen.set(false);

      requestAnimationFrame(() => {
        positionMenu();
        replayOpen();
      });

      return;
    }

    positionMenu();
    replayOpen();
  }
}
